import { DialogActions, DialogContent, Grid, Link, makeStyles, Theme } from '@material-ui/core';
import React, { MouseEventHandler, useCallback, useState } from 'react';
import { RequestStatus } from '../../types';
import { Button } from '../common/Button';
import { DialogTitle } from '../common/Dialog';
import { FontStyle } from '../common/FontStyle';
import { useInputSubmitStyle } from '../common/InputSubmit';
import { Loader } from '../common/Loader';
import { Typography } from '../common/Typography';
import { CopyInput } from './CopyInput';

export interface ShareUploadModalProps {
  onClose: () => void;
  createNewLink: () => Promise<string>;
  sendLinkPerMail: (email: string) => Promise<void>;
  initialLinkToShare: string;
}

const useShareUploadStyles = makeStyles<Theme, { showGenerate: boolean }>({
  dialogContent: {
    overflow: 'visible',
  },
  generate: {
    visibility: ({ showGenerate }) => (showGenerate ? 'visible' : 'hidden'),
  },
});

interface State {
  email: string;
  linkToShare: string;
  generateStatus: RequestStatus;
  sendStatus: RequestStatus;
  showGenerate: boolean;
}

export const ShareUploadModal: React.FunctionComponent<ShareUploadModalProps> = ({
  onClose,
  createNewLink,
  sendLinkPerMail,
  initialLinkToShare,
}) => {
  const inputSubmitStyle = useInputSubmitStyle();
  const [{ email, generateStatus, sendStatus, linkToShare, showGenerate }, setState] =
    useState<State>({
      linkToShare: initialLinkToShare,
      generateStatus: 'none',
      sendStatus: 'none',
      email: '',
      showGenerate: false,
    });
  const styles = useShareUploadStyles({ showGenerate });
  const setStatus = useCallback(
    (key: 'generateStatus' | 'sendStatus', status: RequestStatus) =>
      setState((state) => ({
        ...state,
        [key]: status,
      })),
    [setState],
  );
  const disableSubmit =
    sendStatus === 'loading' || generateStatus === 'loading' || generateStatus === 'error';
  const disableGenerate = generateStatus === 'loading';
  const disableCancel = sendStatus === 'loading' || generateStatus === 'loading';

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (disableSubmit) {
        return;
      }
      setStatus('sendStatus', 'loading');
      try {
        await sendLinkPerMail(email);
        setStatus('sendStatus', 'success');
      } catch (e) {
        console.error(e);
        setStatus('sendStatus', 'error');
      }
    },
    [sendLinkPerMail, setStatus, email, disableSubmit],
  );

  const onGenerateClick: MouseEventHandler = useCallback(
    async (e) => {
      e.preventDefault();
      if (disableGenerate) {
        return;
      }
      setStatus('generateStatus', 'loading');
      try {
        const newLinkToShare = await createNewLink();
        setState((state) => ({
          ...state,
          generateStatus: 'success',
          linkToShare: newLinkToShare,
          showGenerate: false,
        }));
      } catch (e) {
        console.error(e);
        setStatus('generateStatus', 'error');
      }
    },
    [createNewLink, setStatus, setState, disableGenerate],
  );

  const onShowWarning: MouseEventHandler = (e) => {
    e.preventDefault();
    setState((state) => ({ ...state, showGenerate: true }));
  };

  return (
    <>
      <DialogTitle>Share Link</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Grid container direction={'column'} spacing={1}>
          <Grid item lg={12}>
            <Typography paragraph>Everyone with the link can see the uploaded file.</Typography>
          </Grid>
          <Grid item lg={12}>
            <CopyInput text={linkToShare} placeholder='Link to share' readOnly />
          </Grid>
          <Grid item lg={12}>
            <Typography paragraph>
              <Link onClick={onShowWarning} component='button' variant='body1' underline='always'>
                Generate new Link
              </Link>
            </Typography>
            {generateStatus === 'error' ? (
              <Typography paragraph>
                <FontStyle error bold>
                  Error while creating link
                </FontStyle>
              </Typography>
            ) : null}
            {generateStatus === 'success' ? (
              <Typography paragraph>
                <FontStyle success bold>
                  New link created!
                </FontStyle>
              </Typography>
            ) : null}
          </Grid>
          <Grid item lg={12} className={styles.generate} aria-hidden={!showGenerate}>
            <Typography variant='h6'>
              <FontStyle bold error>
                The current link will be removed.
              </FontStyle>
            </Typography>
            <Typography>
              The currently active link will become invalid. This cannot be undone. Are you sure you
              want to continue?
            </Typography>
          </Grid>
          <Grid
            item
            container
            lg={12}
            direction={'row-reverse'}
            className={styles.generate}
            aria-hidden={!showGenerate}
          >
            <Grid item lg={3}>
              <Button
                id='generate'
                type='button'
                onClick={onGenerateClick}
                variant='outlined'
                disabled={generateStatus === 'loading'}
              >
                <Typography variant='h5'>Generate</Typography>
              </Button>
            </Grid>
            <Grid item lg={1}>
              <Loader invisible={generateStatus !== 'loading'} noFlex noMargin />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogTitle>Send Link as Email</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Grid container direction={'column'} spacing={1}>
          <Grid item lg={12}>
            <Typography paragraph>
              The recipient will get the link to the uploaded file directly in an email.
            </Typography>
          </Grid>
          <Grid item lg={12}>
            <form name='sendLink' onSubmit={onSubmit} className={inputSubmitStyle.container}>
              <input
                className={inputSubmitStyle.input}
                value={email}
                type='email'
                onChange={(e) => {
                  const email = e.target.value;
                  setState((state) => ({ ...state, email }));
                }}
                placeholder='user@example.com'
                required
              />
              <div className={inputSubmitStyle.button}>
                <Button name='sendLink' disabled={disableSubmit} type={'submit'}>
                  <Typography>Send Link</Typography>
                </Button>
              </div>
            </form>
          </Grid>
          <Grid item lg={12}>
            {sendStatus === 'error' ? <FontStyle error>Error while sending link</FontStyle> : null}
            {sendStatus === 'success' ? <FontStyle success>Email sent</FontStyle> : null}
            <Loader invisible={sendStatus !== 'loading'} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          id='closeModal'
          type='button'
          onClick={onClose}
          variant='outlined'
          disabled={disableCancel}
        >
          <Typography variant='h5'>Cancel</Typography>
        </Button>
      </DialogActions>
    </>
  );
};
